import cx from "classnames";

import { IPatient } from "common/types";
import { Optional } from "common/utils/types";
import Image from "common/components/Image";

import SelectPanel from "client/common/components/inputs/SelectPanel";
import { RenderContentProps } from "client/common/components/buttons/SelectButton";

import { imageForSpecies } from "../utils";

import styles from "../styles/PatientSelectPanel.module.less";

const noop = () => null;

type RequiredAddProps = { hasAdd: true; onAdd: () => void } | { hasAdd?: false };

export type MultiPatient = { multi: true };

export type Option = IPatient | MultiPatient;

export const isPatient = (option: Option): option is IPatient => {
  return !(option as MultiPatient).multi;
};

type PatientSelectProps = {
  options: Option[];
  value: Optional<Option>;
  onSelect: (value: IPatient) => void;
  onSelectMulti?: () => void;
  hasAdd?: boolean;
  onAdd?: () => void;
  loadingValue?: Optional<Option>;
  disabled?: boolean;
  renderTag?: (value: Option) => React.ReactNode;
} & RequiredAddProps;

const PatientSelectPanel = ({
  options,
  value,
  onSelect,
  onSelectMulti = noop,
  hasAdd = false,
  onAdd = noop,
  loadingValue,
  disabled = false,
  renderTag = noop,
}: PatientSelectProps) => {
  const getKey = (option: Optional<Option>) => {
    if (!option) {
      return "";
    }
    return isPatient(option) ? option.id : "multi";
  };

  const renderOption = (option: Option, { isActive }: RenderContentProps) => {
    const imageSrcs = isPatient(option)
      ? [imageForSpecies(option.species, isActive)]
      : options
          .filter(isPatient)
          .map((o) => imageForSpecies(o.species, false))
          .slice(0, 2);
    return (
      <>
        <div className={styles.optionContent}>
          <span className={cx(styles.petName, !isPatient(option) && styles.multi)}>
            {isPatient(option) ? option.name_display : "Split between pets"}
          </span>
          <div className={styles.species}>
            {imageSrcs.map((src) => (
              <Image
                key={src}
                src={src}
                alt={isPatient(option) ? option.species_display : "Multiple pets"}
              />
            ))}
          </div>
          {renderTag(option)}
        </div>
      </>
    );
  };

  return (
    <SelectPanel<Option>
      variant="horizontal"
      entityName="Pet"
      getKey={getKey}
      getState={(option) =>
        isPatient(option)
          ? {
              isLoading:
                !!loadingValue && isPatient(loadingValue) && loadingValue?.id === option.id,
              disabled,
            }
          : { isLoading: false, disabled: false }
      }
      options={options}
      value={value}
      onClick={(option: Option) => (isPatient(option) ? onSelect(option) : onSelectMulti())}
      hasAdd={hasAdd}
      onAdd={onAdd}
      renderOption={renderOption}
    />
  );
};

export default PatientSelectPanel;
